/**
 * Format a US phone number into "+1 (AAA) PPP-NNNN".
 * - Returns the original input if it's not a valid US number.
 * - Accepts inputs with or without country code and with extra formatting chars.
 */
export function formatUSPhoneNumber(input: string): string {
  if (!input) return "";

  const normalized = normalizeUSPhoneNumberToE164(input);
  if (!normalized) {
    return input;
  }

  const national = normalized.slice(2); // drop "+1"

  const areaCode = national.slice(0, 3);
  const prefix = national.slice(3, 6);
  const lineNumber = national.slice(6, 10);

  return `+1 (${areaCode}) ${prefix}-${lineNumber}`;
}

export type SupportedConferencePhoneConfig = {
  value: string;
  label: string;
  isoCountryCode: string;
  countryCode: string;
  localLengths: number[];
  preserveTrunkZero: boolean;
};

export const SUPPORTED_CONFERENCE_PHONE_CONFIGS: SupportedConferencePhoneConfig[] = [
  {
    value: "US",
    label: "United States",
    isoCountryCode: "US",
    countryCode: "1",
    localLengths: [10],
    preserveTrunkZero: false,
  },
  {
    value: "AE",
    label: "UAE",
    isoCountryCode: "AE",
    countryCode: "971",
    localLengths: [9],
    preserveTrunkZero: true,
  },
  {
    value: "LB",
    label: "Lebanon",
    isoCountryCode: "LB",
    countryCode: "961",
    localLengths: [8],
    preserveTrunkZero: true,
  },
  {
    value: "AR",
    label: "Argentina",
    isoCountryCode: "AR",
    countryCode: "54",
    localLengths: [11],
    preserveTrunkZero: true,
  },
  {
    value: "FR",
    label: "France",
    isoCountryCode: "FR",
    countryCode: "33",
    localLengths: [10],
    preserveTrunkZero: true,
  },
  {
    value: "BR",
    label: "Brazil",
    isoCountryCode: "BR",
    countryCode: "55",
    localLengths: [10, 11],
    preserveTrunkZero: false,
  },
  {
    value: "HN",
    label: "Honduras",
    isoCountryCode: "HN",
    countryCode: "504",
    localLengths: [8],
    preserveTrunkZero: false,
  },
];

function getConferencePhoneConfig(countryCode: string) {
  return SUPPORTED_CONFERENCE_PHONE_CONFIGS.find(
    (config) => config.countryCode === countryCode
  );
}

function normalizeConferenceNationalNumber(
  nationalDigits: string,
  config: SupportedConferencePhoneConfig
): string | null {
  if (!nationalDigits) return null;

  const acceptsLength = (length: number) => config.localLengths.includes(length);
  const shortestLocalLength = Math.min(...config.localLengths);

  if (config.preserveTrunkZero) {
    if (
      nationalDigits.length === shortestLocalLength - 1 &&
      !nationalDigits.startsWith("0")
    ) {
      return `0${nationalDigits}`;
    }

    if (acceptsLength(nationalDigits.length)) {
      return nationalDigits;
    }
  }

  if (acceptsLength(nationalDigits.length)) {
    return nationalDigits;
  }

  return null;
}

export function normalizeConferenceParticipantNumber(
  input: string,
  preferredCountryCode?: string
): string {
  if (!input) return "";

  const digits = input.replace(/\D/g, "");
  if (!digits) return "";

  if (preferredCountryCode) {
    const preferredConfig = getConferencePhoneConfig(preferredCountryCode);
    const normalizedNational = preferredConfig
      ? normalizeConferenceNationalNumber(digits, preferredConfig)
      : null;

    if (preferredConfig && normalizedNational) {
      return `${preferredConfig.countryCode}${normalizedNational}`;
    }
  }

  for (const config of SUPPORTED_CONFERENCE_PHONE_CONFIGS) {
    if (!digits.startsWith(config.countryCode)) continue;

    const nationalDigits = digits.slice(config.countryCode.length);
    const normalizedNational = normalizeConferenceNationalNumber(
      nationalDigits,
      config
    );

    if (normalizedNational) {
      return `${config.countryCode}${normalizedNational}`;
    }
  }

  if (!preferredCountryCode) {
    for (const config of SUPPORTED_CONFERENCE_PHONE_CONFIGS) {
      const normalizedNational = normalizeConferenceNationalNumber(digits, config);
      if (normalizedNational) {
        return `${config.countryCode}${normalizedNational}`;
      }
    }
  }

  return digits;
}

export function parseConferenceParticipantNumber(input: string): {
  countryCode: string;
  phoneNumber: string;
} {
  const normalized = normalizeConferenceParticipantNumber(input);

  for (const config of SUPPORTED_CONFERENCE_PHONE_CONFIGS) {
    if (normalized.startsWith(config.countryCode)) {
      return {
        countryCode: config.countryCode,
        phoneNumber: normalized.slice(config.countryCode.length),
      };
    }
  }

  return {
    countryCode: "1",
    phoneNumber: normalized,
  };
}

export function formatSupportedPhoneNumber(input: string): string {
  if (!input) return "";

  const digits = input.replace(/\D/g, "");

  if (digits.length === 11 && digits.startsWith("1")) {
    return formatUSPhoneNumber(digits);
  }

  if (digits.length === 12 && digits.startsWith("971")) {
    const national = digits.slice(3);
    const part1 = national.slice(0, 2);
    const part2 = national.slice(2, 5);
    const part3 = national.slice(5, 9);
    return `+971 ${part1} ${part2} ${part3}`;
  }

  if (digits.length === 11 && digits.startsWith("961")) {
    const national = digits.slice(3);
    const part1 = national.slice(0, 2);
    const part2 = national.slice(2, 5);
    const part3 = national.slice(5, 8);
    return `+961 ${part1} ${part2} ${part3}`;
  }

  const parsed = parseConferenceParticipantNumber(digits);
  const conferenceConfig = getConferencePhoneConfig(parsed.countryCode);
  if (conferenceConfig && parsed.phoneNumber) {
    return `+${parsed.countryCode} ${parsed.phoneNumber}`;
  }

  return input;
}

//receives a phone number containing country code with plus sign
export function formatPhoneNumber(input: string | number): string {
    let str = String(input).replace(/[^\d+]/g, "");

    const match = str.match(/^\+?(\d{1})(\d{3})(\d+)?$/);
    if (!match) return str; // fallback: return original

    const [, country, area, rest] = match;
    return `+${country} (${area}) ${rest}`;
}

// receives an assumed US number that may or may not contain US country code, checks for validity as a US number
export function isValidUSPhoneNumber(input: string): boolean {
    if (!input) return false;

    const mainPart = input.split(/ext\.?|x/i)[0];

    const digits = mainPart.replace(/\D/g, "");

    if (digits.length === 11 && digits.startsWith("1")) {
        return isValidNanpTenDigit(digits.slice(1));
    }

    if (digits.length === 10) {
        return isValidNanpTenDigit(digits);
    }

    return false;
}

function isValidNanpTenDigit(num: string): boolean {
    if (!/^\d{10}$/.test(num)) return false;

    const area = num.slice(0, 3);
    const exchange = num.slice(3, 6);
    const line = num.slice(6);

    // Area code: NXX where N = 2–9, X = 0–9, middle digit 0–8
    if (!/^[2-9][0-8][0-9]$/.test(area)) return false;

    // Exchange code: NXX where N = 2–9
    if (!/^[2-9][0-9]{2}$/.test(exchange)) return false;

    // Line number: any 4 digits
    if (!/^[0-9]{4}$/.test(line)) return false;

    return true;
}

/**
 * Normalize a US phone number to E.164 (+1XXXXXXXXXX).
 * Returns null if the input is not a valid US number.
 */
export function normalizeUSPhoneNumberToE164(input: string): string | null {
  if (!input) return null;

  const mainPart = input.split(/ext\.?|x/i)[0];
  const digits = mainPart.replace(/\D/g, "");

  if (digits.length === 11 && digits.startsWith("1")) {
    const national = digits.slice(1);
    if (!isValidNanpTenDigit(national)) return null;
    return `+${digits}`; // +1XXXXXXXXXX
  }

  if (digits.length === 10) {
    if (!isValidNanpTenDigit(digits)) return null;
    return `+1${digits}`;
  }

  return null;
}
